/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CartLinePriceEditor, ICartLinePriceEditorResources, PriceComponent } from '@msdyn365-commerce/components';
import { IComponentProps, IGridSettings, IImageSettings, Image } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { ChannelDeliveryOptionConfiguration, ProductDimensionType, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { CartLine, DiscountLine, DiscountLineType, SalesLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ArrayExtensions, getFallbackImageUrl, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, UncontrolledTooltip } from '@msdyn365-commerce-modules/utilities';
import classNames from 'classnames';
import * as React from 'react';
import IncrementalQuantity from './incremental-quantity/IncrementalQuantity';
import { sWGGetInventoryDataAsync } from '../../../../custom-proxy/RetailExtension/DataActionExtension.g';
import { ISWGInventoryData } from '../../../../custom-proxy/RetailExtension/DataServiceEntities.g';
import { splitQuantityThresholdMessage, getAttributeTextValue } from './../../../../utilities/helpers';
// import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
// import { updateCartLinesAsync,updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
// import { getCartState } from '@msdyn365-commerce/global-state';
// import { _makeAttribute,addAttribute } from './../../../../utilities/helpers';
// import { ICartProps as ICartExtensionProps, ICartResources } from '../definition-extensions/cart.ext.props.autogenerated';
// import { ICartConfig as ICartExtensionConfig } from './../../definition-extensions/cart.ext.props.autogenerated';
/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs.
 */
interface IThresholdResource {
    quantityThresholdMessage?: string;
}
export interface ICartlineResourceString {
    /** Display string for discount label. */
    discountStringText: string;

    /** String for size key.  */
    sizeString: string;

    /** String for color key.  */
    colorString: string;

    /** String associated with the configuration product dimension.  */
    configString: string;

    /** String for style key.  */
    styleString: string;

    /** String for amount key.  */
    amountString?: string;

    /** Display string for quantity label.  */
    quantityDisplayString: string;

    /** Display string for quantity label.  */
    inputQuantityAriaLabel: string;

    /** Aria label for the decrement button in quantity component.  */
    decrementButtonAriaLabel: string;

    /** Aria label for the increment button in quantity component.  */
    incrementButtonAriaLabel: string;

    /** Original text screenreader. */
    originalPriceText: string;

    /** Current text screenreader.  */
    currentPriceText: string;

    /** Shipping Charges Text.  */
    shippingChargesText: string;

    /** Price Editor Charges Text.  */
    priceEditorResources?: ICartLinePriceEditorResources & IThresholdResource;
    /** * Shipping text. */
    confirmedShipDateLabel?: string;
    /** * Request Delivery text. */
    requestDeliveryDateLabel?: string;
}

/**
 * Represents cart line data interface.
 */
interface ICartLineData {
    cartLine: CartLine | SalesLine;
    cartState?: ICartState;
    product?: SimpleProduct;
}

/**
 * ICartLineProps: The props required to render cartLineitem.
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    /** The flag to change the quantity component from interactible to static.  */
    disableQuantityInteractivity?: boolean;

    /** The primary image url.  */
    primaryImageUrl?: string;

    /** The product url.  */
    productUrl?: string;

    /** GridSettings for the product image in cartLine.  */
    gridSettings: IGridSettings;

    /** ImageSettings for the product image in cartLine.  */
    imageSettings: IImageSettings;

    /** Boolean flag to indicate if the item is out of stock.  */
    isOutOfStock?: boolean;

    /** Flag to make quantity section editable.  */
    isQuantityEditable?: boolean;

    /** Max quantity for line item.  */
    maxQuantity?: number;

    /** Current quantity for line item.  */
    currentQuantity?: number;

    /** Resource string for the component.  */
    resources: ICartlineResourceString;

    /** configs string for the component.  */
    // config: ICartExtensionConfig;

    /** SalesLine flag.  */
    isSalesLine?: boolean;

    /** Error message to show in place of quantity. */
    errorMessage?: string;

    /** Inventory information label.  */
    inventoryInformationLabel?: string;

    /** Inventory information class name.  */
    inventoryLabelClassName?: string;

    /** Flag to show/hide shipping charges for line items.  */
    showShippingChargesForLineItems?: boolean;

    /** Boolean flag to indicate if cart state status is ready.  */
    isCartStateReady?: boolean;

    /** Chanel Delivery Option configuration is from api.  */
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    /** The telemetry content. */
    telemetryContent?: ITelemetryContent;

    dyelotText?: string;

    // property to check if page is cart
    isCart?: boolean;

    /** Quantity onChange callback.  */
    quantityOnChange?(cartLine: CartLine, newQuantity: number): boolean;
    priceCurrency?: string;
    /** * Sales line confirmed ship date. */
    confirmedShipDate?: string;
    /** * Sales line request delivery date. */
    requestDeliveryDate?: string;
}

/**
 * Represents dimensions strings.
 */
interface IDimensionStrings {
    /** String for size key.  */
    sizeString: string;

    /** String for color key.  */
    colorString: string;

    /** String associated with the configuration product dimension.  */
    configString: string;

    /** String for style key.  */
    styleString: string;

    /** String for amount key.  */
    amountString?: string;
}

const cartLineItemFunctions = {
    /**
     * Render product dimensions.
     * @param product - Product data.
     * @param dimensionStrings - Dimension strings interface.
     * @returns JSX Element.
     */
    renderProductDimensions: (product: SimpleProduct, dimensionStrings: IDimensionStrings) => {
        if (!product.Dimensions) {
            return [];
        }

        return product.Dimensions.map(productDimension => {
            if (productDimension.DimensionTypeValue === ProductDimensionType.Color) {
                return (
                    <div key={`${product.RecordId}ProductDimensions1`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-color'>
                            {dimensionStrings.colorString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === ProductDimensionType.Configuration) {
                return (
                    <div key={`${product.RecordId}ProductDimensions2`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-configuration'>
                            {dimensionStrings.configString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === ProductDimensionType.Size) {
                return (
                    <div key={`${product.RecordId}ProductDimensions3`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-size'>
                            {dimensionStrings.sizeString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === ProductDimensionType.Style) {
                return (
                    <div key={`${product.RecordId}ProductDimensions4`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-style'>
                            {product.IsGiftCard ? dimensionStrings.amountString : dimensionStrings.styleString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }
            return null;
        });
    },

    /**
     * Gets the react node for product unit of price display.
     * @param props - The cart line props.
     * @returns The node representing markup for unit of measure component.
     */
    renderProductUnitPrice: (props: ICartLineProps) => {
        if (!props.data.cartLine.Price) {
            return null;
        }
        const zeroQuantity: number = 0;
        const pricePerText = props.context.app.config.pricePerText;
        return (
            <div className='msc-cart-line__product-unit-price'>
                {props.isSalesLine && (
                    <span>
                        {pricePerText} {props.data.cartLine.UnitOfMeasureSymbol}:{' '}
                    </span>
                )}
                <span>
                    <PriceComponent
                        data={
                            props.data.cartLine.Quantity && props.data.cartLine.Quantity > zeroQuantity
                                ? {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                    />
                </span>
            </div>
        );
    },

    /**
     * Render discount lines.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderDiscountLines: (props: ICartLineProps) => {
        if (!props.data.cartLine.DiscountLines || !ArrayExtensions.hasElements(props.data.cartLine.DiscountLines)) {
            return null;
        }

        /**
         * Get the updated discount percentage.
         * @param discountLine - The discount line.
         * @param cartLine - The cart line.
         * @returns The updated discount percentage.
         */
        const discountPercent = (discountLine: DiscountLine, cartLine: CartLine): number => {
            const numberZero = 0;
            const decimalPrecision = 2;
            const fullPercentage = 100;
            let updatedPercent: number = discountLine.Percentage ?? numberZero;

            if (
                (discountLine.Percentage === numberZero ||
                    (discountLine.DiscountLineTypeValue && discountLine.DiscountLineTypeValue === DiscountLineType.TenderTypeDiscount)) &&
                cartLine.Price &&
                cartLine.Price > numberZero &&
                cartLine.Quantity &&
                cartLine.Quantity !== numberZero
            ) {
                const effectiveAmount = discountLine.EffectiveAmount ?? numberZero;
                updatedPercent = Number(
                    ((effectiveAmount / (cartLine.Price * cartLine.Quantity)) * fullPercentage).toFixed(decimalPrecision)
                );
            }

            return updatedPercent;
        };

        return props.data.cartLine.DiscountLines.map((discountLine, index) => {
            return (
                <div key={discountLine.OfferId ?? index} className='msc-cart-line__product-discount'>
                    <span>{discountLine.OfferName ? discountLine.OfferName : ''}</span>
                    <span className='msc-cart-line-item-product-discount-price'>
                        <PriceComponent
                            data={
                                props.isSalesLine
                                    ? {
                                          price: {
                                              // @ts-expect-error -- Auto suppressed.
                                              CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                          }
                                      }
                                    : {
                                          price: {
                                              CustomerContextualPrice: discountLine.EffectiveAmount,
                                              BasePrice: discountLine.EffectiveAmount
                                          }
                                      }
                            }
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                            currencyCode={props.priceCurrency}
                        />
                    </span>
                    <span className='msc-cart-line__product-discount-percentage'>
                        {` (${discountLine.Percentage !== undefined ? discountPercent(discountLine, props.data.cartLine) : ''}%)`}
                    </span>
                </div>
            );
        });
    },

    /**
     * Render inventory label.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderInventoryLabel: (props: ICartLineProps) => {
        const inventoryCssName = props.inventoryLabelClassName
            ? `msc-cart-line__product-inventory-label ${props.inventoryLabelClassName}`
            : 'msc-cart-line__product-inventory-label';

        return <p className={inventoryCssName}>{props.inventoryInformationLabel}</p>;
    },

    /**
     * Render shipping label.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderShippingLabel: (props: ICartLineProps) => {
        const pickupDeliveryModeCode = props.context.request.channel?.PickupDeliveryModeCode;
        const channelDeliveryOptionConfig = props.channelDeliveryOptionConfig;
        const cartline = props.data.cartLine;
        let hasShippingMethod = false;
        if (channelDeliveryOptionConfig !== undefined) {
            hasShippingMethod = !!(
                cartline.DeliveryMode &&
                channelDeliveryOptionConfig.PickupDeliveryModeCodes?.some(deliveryMode => deliveryMode !== cartline.DeliveryMode)
            );
        } else {
            hasShippingMethod = !!(cartline.DeliveryMode && cartline.DeliveryMode !== pickupDeliveryModeCode);
        }
        if (!hasShippingMethod) {
            return undefined;
        }

        const shippingChargeLines = (cartline.ChargeLines ?? []).filter(chargeLine => chargeLine.IsShipping);

        if (!ArrayExtensions.hasElements(shippingChargeLines)) {
            return undefined;
        }

        const defaultCharge: number = 0;
        const freightFee = shippingChargeLines.reduce((chargeTotal, chargeLine) => {
            return chargeTotal + (chargeLine.CalculatedAmount ?? defaultCharge);
        }, defaultCharge);

        const priceComponent = (
            <PriceComponent
                data={{
                    price: {
                        CustomerContextualPrice: freightFee
                    }
                }}
                freePriceText='Free'
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                className='msc-cart-line__freight-actual'
                currencyCode={props.priceCurrency}
            />
        );
        return (
            <>
                <label className='msc-cart-line__freight-label'>{`${props.resources.shippingChargesText}:`}</label>
                <span className='shipping-value'>{priceComponent}</span>
            </>
        );
    },

    /**
     * Render other charges.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderOtherCharges: (props: ICartLineProps) => {
        const cartline = props.data.cartLine;
        // const otherCharges = cartline.ChargeLines?.filter((chargeline) => !chargeline.IsShipping);
        // get repack charges lines
        const repackCharges = cartline.ChargeLines?.filter(chargeline => !chargeline.IsShipping);
        const totalRepackCharges = repackCharges?.reduce((totalCharges, currentValue) => {
            return totalCharges + (currentValue.CalculatedAmount || 0);
        }, 0);
        const translateRepackingLabel = () => {
            const { repackChargesLabel, itRepackChargesLabel, deRepackChargesLabel } = props.context.app.config;
            const locale = props.context.request.locale;
            if (locale === 'it') {
                return itRepackChargesLabel;
            } else if (locale === 'de') {
                return deRepackChargesLabel;
            } else {
                return repackChargesLabel;
            }
        };
        // const repackChargeLabel = repackCharges && repackCharges.length > 0 && repackCharges[0].Description;
        return totalRepackCharges && totalRepackCharges !== 0 ? (
            <div className='msc-cart-line__other-charges'>
                <label className='msc-cart-line__other-charges-label'>{`${translateRepackingLabel()}:`}</label>
                <span className='other-charge-value'>
                    <PriceComponent
                        data={{
                            price: {
                                CustomerContextualPrice: totalRepackCharges
                            }
                        }}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className='msc-cart-line__other-charges-actual'
                        currencyCode={props.priceCurrency}
                    />
                </span>
            </div>
        ) : null;
        // return otherCharges?.map((otherCharge, index) => {
        //     const itemIndex = index;
        //     return otherCharge.CalculatedAmount ? (
        //         <div className='msc-cart-line__other-charges' key={itemIndex}>
        //             <label className='msc-cart-line__other-charges-label'>
        //                 {`${String(otherCharge.Description)}:`}
        //             </label>
        //             <span className='other-charge-value'>
        //                 <PriceComponent
        //                     data={{
        //                         price: {
        //                             CustomerContextualPrice: otherCharge.CalculatedAmount
        //                         }
        //                     }}
        //                     context={props.context}
        //                     id={props.id}
        //                     typeName={props.typeName}
        //                     className='msc-cart-line__other-charges-actual'
        //                 />
        //             </span>
        //         </div>
        //     ) : '';
        // }) ?? undefined;
    },
    renderConfirmedShipDate: (props: ICartLineProps) => {
        if (!props.isSalesLine || props.confirmedShipDate === undefined || StringExtensions.isNullOrEmpty(props.confirmedShipDate)) {
            return null;
        }
        const shipDateCssName = 'msc-cart-line__confirmed-ship-date';
        return (
            <>
                <span className={shipDateCssName}>{props.resources.confirmedShipDateLabel}</span>
                <span className={shipDateCssName}>{props.confirmedShipDate}</span>
            </>
        );
    },
    renderRequestDeliveryDate: (props: ICartLineProps) => {
        if (!props.isSalesLine || props.requestDeliveryDate === undefined || StringExtensions.isNullOrEmpty(props.requestDeliveryDate)) {
            return null;
        }
        const reqDateCssName = 'msc-cart-line__request-delivery-date';
        return (
            <>
                <span className={reqDateCssName}>{props.resources.requestDeliveryDateLabel}</span>
                <span className={reqDateCssName}>{props.requestDeliveryDate}</span>
            </>
        );
    }
};

/**
 * Render cartline component.
 * @param props - The cart line props.
 * @returns JSX element.
 */
const CartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const { isSalesLine, productUrl, resources } = props;
    const { product, cartLine } = props.data;
    const [inventoryData, setInventoryData] = React.useState<ISWGInventoryData[] | null>(null);
    // const tooltipRef = React.useRef<HTMLSpanElement>(null);
    const tooltipRef = React.useRef<HTMLAnchorElement>(null);
    const WeighttooltipRef = React.useRef<HTMLAnchorElement>(null);

    const [totalAvailableInventory, setTotalAvailableInventory] = React.useState<number>(0);

    const destructDimensionStrings = {
        sizeString: resources.sizeString,
        colorString: resources.colorString,
        styleString: resources.styleString,
        configString: resources.configString,
        amountString: resources.amountString
    };
    const fallbackImageUrl =
        product &&
        getFallbackImageUrl(
            product.ItemId,
            props.context.actionContext.requestContext.apiSettings,
            props.context.app?.config?.OmniChannelMedia
        );
    const productDimensions = product && cartLineItemFunctions.renderProductDimensions(product, destructDimensionStrings);
    const renderProductUnitPrice = cartLineItemFunctions.renderProductUnitPrice(props);
    const imageSettings = props.imageSettings;
    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }
    const renderDiscountLines = cartLineItemFunctions.renderDiscountLines(props);
    const renderInventoryLabel = cartLineItemFunctions.renderInventoryLabel(props);
    const renderShippingLabel = cartLineItemFunctions.renderShippingLabel(props);
    const renderOtherCharges = cartLineItemFunctions.renderOtherCharges(props);
    const renderConfirmedShipDate = cartLineItemFunctions.renderConfirmedShipDate(props);
    const payLoad = getPayloadObject('click', props.telemetryContent!, '', product?.RecordId.toString());
    const productAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);
    const productName = product?.Description ?? cartLine.Description;
    const imgeClassName = props.data.cartLine.IsInvoiceLine ? 'msc-cart-line__invoice-image' : 'msc-cart-line__product-image';
    const cartLineContentClassName = props.data.cartLine.IsInvoiceLine ? 'msc-cart-line__invoice-content' : 'msc-cart-line__content';
    // const unitOfMeasure = product?.DefaultUnitOfMeasure?.toLowerCase() ?? cartLine.UnitOfMeasureSymbol?.toLowerCase();
    // const PieceUnitOfMeasure = unitOfMeasure === 'pcs' ? true : false || false;
    const fetchInventoryInfo = React.useCallback(async () => {
        if (product && product.RecordId && props.currentQuantity !== totalAvailableInventory) {
            if (inventoryData) {
                setInventoryData(null);
            }
            const inventory = await sWGGetInventoryDataAsync(
                { callerContext: props.context.actionContext },
                product?.RecordId,
                props.currentQuantity || 1
            );

            setInventoryData(inventory);
            const totalAvailable = inventory.reduce((total, data) => {
                return total + data.AvailPhysical;
            }, 0);
            setTotalAvailableInventory(totalAvailable);
        }
    }, [props.currentQuantity]);

    React.useEffect(() => {
        if (props.isCart) {
            fetchInventoryInfo();
        }
    }, [props.currentQuantity]);

    // VSI - Customization Start

    // const _setProductAttribute = async () => {
    //     const cartState = await getCartState(props.context.actionContext);
    //     const productBatchIdAndQuantity = props.context.app.config.productQuantityAndBatchAttribute && props.context.app.config.productQuantityAndBatchAttribute || 'Batch';
    //     // VSI - Customization to add Product attributes to display weight and manufacuture on order details
    //     if (cartState.cart && cartState.cart.CartLines && inventoryData) {
    //         const productQuantityAndBatchRecord = inventoryData.map((inventoryItem)=>{
    //             const record = {
    //                 ConsumedQuantity: inventoryItem.ConsumedQuantity,
    //                 InventBatchId: inventoryItem.InventBatchId,
    //             }
    //             return record
    //         })
    //         const cartLines = cartState.cart.CartLines.filter(cartLineItem => cartLineItem.ProductId === product?.RecordId);
    //         if (cartLines && cartLines.length > 0) {
    //             const newCartLine = cartLines[cartLines.length - 1];
    //             const productBatchIdAndQuantityAttr = _makeAttribute(productBatchIdAndQuantity,JSON.stringify(productQuantityAndBatchRecord) || '');
    //             let currentAttributes: AttributeValueBase[] = newCartLine.AttributeValues || [];
    //             currentAttributes = addAttribute(currentAttributes, productBatchIdAndQuantityAttr);
    //             newCartLine.AttributeValues = currentAttributes;
    //             const updatedCart = await updateCartLinesAsync({ callerContext: props.context.actionContext }, cartState.cart.Id, [newCartLine])
    //                 // .then()
    //                 // .catch(error => props.context.actionContext.telemetry.exception(error));
    //             await updateAsync({ callerContext: props.context.actionContext }, updatedCart);
    //         }
    //     }
    // };

    // React.useEffect(() => {
    //     if (inventoryData?.length! > 0) {
    //         _setProductAttribute();
    //     }
    // }, [inventoryData]);
    // VSI - Customization End

    /**
     * Render other charges.
     * @param newValue - The cart line props.
     * @returns Boolean.
     */
    const onChange = (newValue: number): boolean => {
        if (props.quantityOnChange) {
            props.quantityOnChange(props.data.cartLine, newValue);
        }
        return true;
    };

    /**
     * Generate error message.
     * @returns JSX element.
     */
    // const generateErrorMessage = (): JSX.Element | null => {
    //     if (props.errorMessage) {
    //         return (
    //             <div className='msc-alert__header'>
    //                 <span className='msi-exclamation-triangle' />
    //                 <span>
    //                     {props.errorMessage}
    //                 </span>
    //             </div>
    //         );
    //     }

    //     return null;
    // };

    /**
     * Render Saving component.
     * @returns JSX element.
     */
    const renderSaving = (): JSX.Element | null => {
        if (!props.data.cartLine.DiscountAmount) {
            return null;
        }
        const discountAmountZero: number = 0;

        if (props.data.cartLine.DiscountAmount > discountAmountZero) {
            return (
                <>
                    <span className='msc-cart-line__product-savings-label'>{`${props.resources.discountStringText || 'Discount'}`}</span>
                    <PriceComponent
                        data={
                            props.isSalesLine
                                ? {
                                      price: {
                                          // @ts-expect-error -- Auto suppressed.
                                          CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.DiscountAmountWithoutTax,
                                          BasePrice: props.data.cartLine.DiscountAmount
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className='msc-cart-line__product-savings-text'
                    />
                </>
            );
        }
        return null;
    };

    /**
     * Gets the react node for product unit of measure display.
     * @returns The node representing markup for unit of measure component.
     */
    const renderUnitOfMeasure = () => {
        if (props.data.cartLine.IsInvoiceLine) {
            return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Do not need type check for appsettings
        if (!props.context.app.config.unitOfMeasureDisplayType || props.context.app.config.unitOfMeasureDisplayType === 'none') {
            return undefined;
        }

        if (!product || !product.DefaultUnitOfMeasure) {
            return undefined;
        }

        return (
            <div className='msc-cart-line__product-unit-of-measure'>
                <span>{product.DefaultUnitOfMeasure?.toLowerCase()}</span>
            </div>
        );
    };

    /**
     * Generate quantity and price component.
     * @returns JSX element.
     */
    const generateQuantityAndPrice = (): JSX.Element[] | null => {
        const nodes = [];
        let defaultMaxQuantity: number = props.context.app.config.maxQuantityForCartLineItem;
        const singleQuantity: number = 1;
        const unitOfMeasure = renderUnitOfMeasure();
        // No quantity selector for invoice line
        if (!props.data.cartLine.IsInvoiceLine) {
            if (props.data.product && props.isQuantityEditable) {
                nodes.push(
                    <div className='msc-cart-line__product-quantity'>
                        <div className='msc-cart-line__product-quantity-label'>
                            {/* {resources.quantityDisplayString} */}
                            {props.data.cartLine.UnitOfMeasureSymbol?.toLowerCase()}:
                            {props.isCart && (
                                <>
                                    <span className='msc-cart-line__product-quantity-info-btn'>
                                        {/* <a href={cartUnitOfMeasureIconLink} target='_blank' rel='noreferrer' ref={tooltipRef}> */}
                                        <i className='msi-InfoCircle'></i>
                                        {/* </a> */}
                                    </span>
                                    {additionalWeightInfotooltip && (
                                        <UncontrolledTooltip target={tooltipRef}>{additionalWeightInfotooltip}</UncontrolledTooltip>
                                    )}
                                </>
                            )}
                        </div>

                        {/* {generateErrorMessage()} */}

                        <IncrementalQuantity
                            id={`msc-cart-line__quantity_${props.data.product.RecordId}-
                            ${String(props.data.cartLine.DeliveryMode)}-${String(props.data.cartLine.LineId)}`}
                            max={props.maxQuantity ?? defaultMaxQuantity}
                            currentCount={props.currentQuantity}
                            onChange={onChange}
                            inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                            decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                            incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                            key={props.data.cartLine.LineId}
                            // disabled={!props.isCartStateReady || props.isOutOfStock}
                            disabled={true}
                            isGiftCard={props.data.product.IsGiftCard ?? props.isOutOfStock}
                            telemetryContent={props.telemetryContent}
                        />
                        {unitOfMeasure}
                    </div>
                );
            } else {
                nodes.push(
                    <div
                        className={classNames('msc-cart-line__quantity', {
                            'single-quantity': props.data.cartLine.Quantity === singleQuantity
                        })}
                    >
                        {unitOfMeasure}
                        <label className='quantity-label'>
                            {/* {resources.quantityDisplayString} */}
                            {props.data.cartLine.UnitOfMeasureSymbol?.toLowerCase()}
                            {': '}
                        </label>

                        {/* {generateErrorMessage()} */}

                        <span className='quantity-value'>{props.data.cartLine.Quantity}</span>
                    </div>
                );
            }
        }

        nodes.push(
            <div className='msc-cart-line__product-savings'>
                <PriceComponent
                    data={
                        isSalesLine
                            ? {
                                  price: {
                                      // @ts-expect-error -- Auto suppressed.
                                      CustomerContextualPrice: props.data.cartLine.NetAmount,
                                      BasePrice: props.data.cartLine.Price
                                  }
                              }
                            : {
                                  price: {
                                      CustomerContextualPrice: (props.data.cartLine as CartLine).ExtendedPrice,
                                      BasePrice: props.data.cartLine.NetPrice
                                  }
                              }
                    }
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    className='msc-cart-line__product-savings-actual'
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                />
                {props.isOutOfStock ? renderInventoryLabel : null}
            </div>
        );

        return nodes;
    };
    const quantityThresholdAttributeLabel = props.context.app.config.quantityThresholdAttributeName;
    // const hideQuantityThresholdMessage = props.context.app.config.hideQuantityThresholdMessage;
    // const showQuantityThresholdMessage = !hideQuantityThresholdMessage;
    const locale = props.context.request.locale;
    const { thresholdPrefix, thresholdContent, thresholdPostfix } = splitQuantityThresholdMessage(
        resources.priceEditorResources?.quantityThresholdMessage,
        locale
    );
    const quantityThresholdValue = +getAttributeTextValue(props.data.cartLine.AttributeValues!, quantityThresholdAttributeLabel);
    const displayThresholdMessage = !props.context.app.config.hideThresholdMessage;
    const additionalWeightInfoLink = props.context.app.config.additionalWeightInfoLink;
    const additionalWeightInfotooltip =
        props.context.app.config.additionalWeightInfotooltip && props.context.app.config.additionalWeightInfotooltip;
    // const cartUnitOfMeasureIconLink = props.config.cartUnitOfMeasureIconLink! && props.config.cartUnitOfMeasureIconLink?.destinationUrl && props.config.cartUnitOfMeasureIconLink.destinationUrl;
    // const cartUnitOfMeasureIconTooltip = props.config.cartUnitOfMeasureIconTooltip && props.config.cartUnitOfMeasureIconTooltip;
    return (
        <div className='msc-cart-line'>
            <a className={imgeClassName} href={productUrl} aria-label={productName}>
                <Image
                    requestContext={props.context.actionContext.requestContext}
                    aria-hidden='true'
                    src={props.primaryImageUrl ?? 'empty'}
                    fallBackSrc={fallbackImageUrl}
                    altText={productName}
                    gridSettings={props.gridSettings}
                    imageSettings={imageSettings}
                    loadFailureBehavior='empty'
                />
            </a>
            <div className={cartLineContentClassName}>
                <div className='msc-cart-line__product'>
                    <a className='msc-cart-line__product-title' href={productUrl} {...productAttribute}>
                        {productName}
                    </a>
                    {ArrayExtensions.hasElements(productDimensions) ? (
                        <div className='msc-cart-line__product-variants'>{productDimensions}</div>
                    ) : (
                        ''
                    )}
                    <div className='msc-cart-line__product-price'>
                        <PriceComponent
                            data={
                                isSalesLine
                                    ? {
                                          price: {
                                              // @ts-expect-error -- Auto suppressed.
                                              CustomerContextualPrice: props.data.cartLine.NetAmount,
                                              BasePrice: props.data.cartLine.Price
                                          }
                                      }
                                    : {
                                          price: {
                                              CustomerContextualPrice: (props.data.cartLine as CartLine).ExtendedPrice,
                                              BasePrice: props.data.cartLine.NetPrice
                                          }
                                      }
                            }
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                            className='msc-cart-line__product-discount-value'
                            currencyCode={props.priceCurrency}
                        />
                        {renderUnitOfMeasure()}
                    </div>
                    {renderProductUnitPrice}
                    {renderInventoryLabel}
                    {props.showShippingChargesForLineItems && <div className='msc-cart-line__freight'>{renderShippingLabel}</div>}
                    <div className='show-only-checkout'>{generateQuantityAndPrice()}</div>
                    {renderOtherCharges}
                    {inventoryData && (
                        <ol className='inventory-information'>
                            {inventoryData.map((data, i) => {
                                return (
                                    <li key={i}>
                                        <ul>
                                            {data.ConsumedQuantity && (
                                                <li>
                                                    {props.dyelotText}: {parseFloat(data.ConsumedQuantity).toFixed(2)}{' '}
                                                    {product?.DefaultUnitOfMeasure?.toLowerCase()}
                                                </li>
                                            )}
                                            {data.PackingTypeName !== '' && <li>{data.PackingTypeName}</li>}
                                            {data.BusinessModeValue !== '' && <li>{data.BusinessModeValue}</li>}
                                        </ul>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                    {renderDiscountLines && (
                        <div className='discounts-generic'>
                            {renderDiscountLines} <span className='seperator'>,</span>
                            {renderSaving()}
                        </div>
                    )}
                    {renderConfirmedShipDate}
                </div>
                {generateQuantityAndPrice()}
                {props.data.cartLine.IsInvoiceLine && props.data.cartState && props.resources.priceEditorResources && (
                    <CartLinePriceEditor
                        className='msc-cart-line__price-editor-container'
                        context={props.context}
                        resources={props.resources.priceEditorResources}
                        cartState={props.data.cartState}
                        cartLine={props.data.cartLine}
                    />
                )}
            </div>
            {displayThresholdMessage && (
                <p className='quantity-threshold-message'>
                    <a href={additionalWeightInfoLink.destinationUrl} target='_blank' rel='noreferrer' ref={WeighttooltipRef}>
                        <i className='msi-InfoCircle'></i>
                    </a>
                    {additionalWeightInfotooltip && (
                        <UncontrolledTooltip target={WeighttooltipRef}>{additionalWeightInfotooltip}</UncontrolledTooltip>
                    )}
                    <span>
                        {thresholdPrefix} {props.data.cartLine.Quantity! - quantityThresholdValue} {thresholdContent}{' '}
                        {quantityThresholdValue} {thresholdPostfix}
                    </span>
                </p>
            )}
        </div>
    );
};

export default CartLine;
